import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  PenLine, RefreshCw, Send, Download, RotateCcw, Bell, Loader2,
} from 'lucide-react';
import {
  sendOfferForESign,
  getOfferESignStatus,
  downloadSignedAnnexure,
  recallOfferESign,
  remindOfferESign,
  getOfferESignLog,
} from '../api';

const STATUS_STYLES = {
  NOT_SENT: 'bg-slate-100 text-slate-700 border-slate-200',
  DRAFT: 'bg-amber-100 text-amber-800 border-amber-200',
  SENT: 'bg-blue-100 text-blue-800 border-blue-200',
  VIEWED: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  SIGNED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  DECLINED: 'bg-red-100 text-red-700 border-red-200',
  EXPIRED: 'bg-orange-100 text-orange-800 border-orange-200',
  RECALLED: 'bg-zinc-200 text-zinc-700 border-zinc-300',
  UNKNOWN: 'bg-slate-100 text-slate-600 border-slate-200',
};

const fmtTs = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function ZohoSignDialog({ open, onOpenChange, offer }) {
  const [esign, setEsign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [log, setLog] = useState([]);

  // Send form
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [notes, setNotes] = useState('');
  const [expirationDays, setExpirationDays] = useState(14);
  const [reminderPeriod, setReminderPeriod] = useState(5);

  const loadStatus = useCallback(async (refresh = false) => {
    if (!offer?.id) return;
    setLoading(true);
    try {
      const { data } = await getOfferESignStatus(offer.id, refresh);
      setEsign(data.esign || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load e-sign status');
    } finally {
      setLoading(false);
    }
  }, [offer?.id]);

  const loadLog = useCallback(async () => {
    if (!offer?.id) return;
    try {
      const { data } = await getOfferESignLog(offer.id);
      setLog(data.log || []);
    } catch { /* non-fatal */ }
  }, [offer?.id]);

  useEffect(() => {
    if (open && offer) {
      setRecipientEmail(offer.candidate_email || '');
      setRecipientName(offer.candidate_name || '');
      setCcEmails('');
      setNotes('');
      loadStatus(false);
      loadLog();
    }
  }, [open, offer, loadStatus, loadLog]);

  const status = esign?.status || 'NOT_SENT';
  const canSend = !esign?.request_id || ['DECLINED', 'EXPIRED', 'RECALLED'].includes(status);
  const inProgress = ['SENT', 'VIEWED', 'DRAFT'].includes(status);
  const isSigned = status === 'SIGNED';

  const handleSend = async () => {
    if (!recipientEmail || !recipientEmail.includes('@')) {
      toast.error('Please enter a valid recipient email');
      return;
    }
    setSending(true);
    try {
      const ccList = ccEmails
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter((s) => s.includes('@'));
      const { data } = await sendOfferForESign(offer.id, {
        recipient_email: recipientEmail,
        recipient_name: recipientName || undefined,
        cc_emails: ccList,
        notes: notes || undefined,
        expiration_days: Number(expirationDays) || 14,
        reminder_period: Number(reminderPeriod) || 5,
      });
      toast.success(`Sent to ${data.recipient_email} for e-signature.`);
      await loadStatus(false);
      await loadLog();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to send for e-signature';
      toast.error(detail, { duration: 6000 });
    } finally {
      setSending(false);
    }
  };

  const handleRefresh = () => loadStatus(true);

  const handleDownloadSigned = async () => {
    setActionBusy(true);
    try {
      const response = await downloadSignedAnnexure(offer.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Signed_Annexure_${(offer.candidate_name || 'candidate').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Signed annexure downloaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to download signed PDF');
    } finally {
      setActionBusy(false);
    }
  };

  const handleRecall = async () => {
    if (!window.confirm('Recall this signing request? Candidate will no longer be able to sign.')) return;
    setActionBusy(true);
    try {
      await recallOfferESign(offer.id, 'Recalled by HR via HRMS');
      toast.success('Request recalled');
      await loadStatus(false);
      await loadLog();
    } catch (e) {
      // If backend returns 409 (conflict / state mismatch), it has already
      // synced our local DB from Zoho — refresh the dialog so the user sees
      // the correct status (e.g. SIGNED) without manually refreshing.
      if (e?.response?.status === 409) {
        await loadStatus(false);
        await loadLog();
      }
      toast.error(e?.response?.data?.detail || 'Failed to recall');
    } finally {
      setActionBusy(false);
    }
  };

  const handleRemind = async () => {
    setActionBusy(true);
    try {
      await remindOfferESign(offer.id);
      toast.success('Reminder sent to candidate');
      await loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to remind');
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="zoho-sign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine size={20} className="text-indigo-600" />
            E-Sign Annexure via Zoho Sign
          </DialogTitle>
          <DialogDescription>
            Send the personalised salary annexure to{' '}
            <span className="font-medium">{offer?.candidate_name}</span> for electronic signature.
          </DialogDescription>
        </DialogHeader>

        {/* Status panel */}
        <div className="rounded-lg border bg-slate-50 p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700">Status:</span>
              <Badge
                className={`border ${STATUS_STYLES[status] || STATUS_STYLES.UNKNOWN}`}
                data-testid="esign-status-badge"
              >
                {status}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={loading || !esign?.request_id}
              data-testid="esign-refresh-status"
            >
              {loading ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} />}
              <span className="ml-1.5">Refresh</span>
            </Button>
          </div>

          {esign?.request_id && (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
              <div><span className="font-medium">Recipient:</span> {esign.recipient_email || '—'}</div>
              <div><span className="font-medium">Sent:</span> {fmtTs(esign.sent_at)}</div>
              <div><span className="font-medium">Last event:</span> {fmtTs(esign.last_event_at)}</div>
              <div><span className="font-medium">Completed:</span> {fmtTs(esign.completed_at)}</div>
              <div className="col-span-2 truncate"><span className="font-medium">Zoho Request ID:</span> {esign.request_id}</div>
            </div>
          )}
        </div>

        {/* Send form */}
        {canSend && (
          <div className="space-y-3 border-t pt-4">
            <h4 className="text-sm font-semibold text-slate-700">Send for signature</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="zs-recipient-name">Recipient Name</Label>
                <Input
                  id="zs-recipient-name"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  data-testid="zs-recipient-name"
                />
              </div>
              <div>
                <Label htmlFor="zs-recipient-email">Recipient Email *</Label>
                <Input
                  id="zs-recipient-email"
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  data-testid="zs-recipient-email"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="zs-cc">CC (comma-separated, optional)</Label>
              <Input
                id="zs-cc"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="hr@tnservices.in, manager@tnservices.in"
                data-testid="zs-cc-emails"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="zs-expiry">Expiration (days)</Label>
                <Input
                  id="zs-expiry"
                  type="number"
                  min="1"
                  max="180"
                  value={expirationDays}
                  onChange={(e) => setExpirationDays(e.target.value)}
                  data-testid="zs-expiration-days"
                />
              </div>
              <div>
                <Label htmlFor="zs-reminder">Reminder every (days)</Label>
                <Input
                  id="zs-reminder"
                  type="number"
                  min="1"
                  max="30"
                  value={reminderPeriod}
                  onChange={(e) => setReminderPeriod(e.target.value)}
                  data-testid="zs-reminder-period"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="zs-notes">Note to recipient (optional)</Label>
              <Textarea
                id="zs-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Please review and sign your Salary Annexure."
                rows={2}
                data-testid="zs-notes"
              />
            </div>
          </div>
        )}

        {/* In-progress actions */}
        {inProgress && (
          <div className="border-t pt-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRemind}
              disabled={actionBusy}
              data-testid="zs-remind-btn"
            >
              <Bell size={14} className="mr-1.5" /> Send Reminder
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecall}
              disabled={actionBusy}
              className="text-red-600 hover:text-red-700"
              data-testid="zs-recall-btn"
            >
              <RotateCcw size={14} className="mr-1.5" /> Recall
            </Button>
          </div>
        )}

        {/* Signed - download */}
        {isSigned && (
          <div className="border-t pt-4">
            <Button
              onClick={handleDownloadSigned}
              disabled={actionBusy}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="zs-download-signed-btn"
            >
              <Download size={16} className="mr-1.5" />
              Download Signed PDF
            </Button>
          </div>
        )}

        {/* Audit log */}
        {log.length > 0 && (
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Activity Log</h4>
            <div className="max-h-48 overflow-y-auto text-xs space-y-1.5">
              {log.map((e, idx) => (
                <div key={e.id || idx} className="flex items-start gap-2 border-l-2 border-indigo-200 pl-2">
                  <span className="text-slate-400 shrink-0">{fmtTs(e.at)}</span>
                  <span className="font-medium text-slate-700">{e.event_type}</span>
                  {e.mapped_status && (
                    <Badge variant="outline" className="text-[10px] py-0">{e.mapped_status}</Badge>
                  )}
                  {e.actor_user_name && (
                    <span className="text-slate-500">— {e.actor_user_name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="zs-close-btn">
            Close
          </Button>
          {canSend && (
            <Button
              onClick={handleSend}
              disabled={sending || !recipientEmail}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="zs-send-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="animate-spin mr-1.5" size={16} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} className="mr-1.5" />
                  Send for E-Signature
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
